import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { useCallback, useMemo, useState } from "react";
import { JsonResponse } from "TypeGen/json-response";
import { fetchApi } from "../../services/api";

type CarrierEventOption = {
  value: number;
  label: string;
};

const carrierEventTypeOptions: CarrierEventOption[] = [
  { value: 5, label: "Load Turned Back" },
  { value: 12, label: "Bid Placed Incorrectly" },
  { value: 13, label: "Safty Probation" },
];

type Props = {
  carrierId: string;
  onClose: () => void;
  onCreated: () => void;
};

export const CreateCarrierScoreEventDialog = ({ carrierId, onClose, onCreated }: Props) => {
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [createEventType, setCreateEventType] = useState<number | null>(null);
  const [createDescription, setCreateDescription] = useState("");
  const [createOrderNumber, setCreateOrderNumber] = useState("");
  const [createQuoteNumber, setCreateQuoteNumber] = useState("");

  const handleCreateEventTypeChange = useCallback((event: DropDownListChangeEvent) => {
    const option = event.value as CarrierEventOption | null;
    setCreateEventType(option?.value ?? null);
  }, []);

  const handleCreateEvent = useCallback(() => {
    const parsedCarrierId = Number(carrierId);
    if (!parsedCarrierId) {
      alert("Carrier is required.");
      return;
    }

    const description = createDescription.trim();
    if (!description) {
      alert("Description is required.");
      return;
    }

    const orderNumberText = createOrderNumber.trim();
    let orderNumber: number | null = null;
    if (orderNumberText.length > 0) {
      const parsedOrderNumber = Number(orderNumberText);
      if (!Number.isInteger(parsedOrderNumber) || parsedOrderNumber <= 0) {
        alert("Order Number must be a positive whole number.");
        return;
      }
      orderNumber = parsedOrderNumber;
    }

    const quoteNumberText = createQuoteNumber.trim();
    let quoteNumber: number | null = null;
    if (quoteNumberText.length > 0) {
      const parsedQuoteNumber = Number(quoteNumberText);
      if (!Number.isInteger(parsedQuoteNumber) || parsedQuoteNumber <= 0) {
        alert("Quote Number must be a positive whole number.");
        return;
      }
      quoteNumber = parsedQuoteNumber;
    }

    setCreatingEvent(true);
    fetchApi("/api/Asset/CreateCarrierScoreEvent", {
      CarrierID: parsedCarrierId,
      EventType: createEventType,
      Description: description,
      OrderNumber: orderNumber,
      QuoteNumber: quoteNumber
    }, "post")
      .then((response: JsonResponse) => {
        if (response.Success) {
          onCreated();
          onClose();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        if (!e?.status) alert("Unable to create score event");
      })
      .finally(() => setCreatingEvent(false));
  }, [carrierId, createDescription, createEventType, createOrderNumber, createQuoteNumber, onClose, onCreated]);

  const selectedCreateEventType = useMemo(
    () => carrierEventTypeOptions.find(option => option.value === createEventType) ?? null,
    [createEventType]
  );

  return (
    <Dialog title="Create Carrier Score Event" onClose={onClose} className="dialog-w525">
      <div className="mb-3">
        <label className="form-label mb-1">Event Type</label>
        <DropDownList
          data={carrierEventTypeOptions}
          textField="label"
          dataItemKey="value"
          value={selectedCreateEventType}
          onChange={handleCreateEventTypeChange}
          style={{ width: "100%" }}
        />
      </div>
      <div className="mb-3">
        <label className="form-label mb-1">Description</label>
        <textarea
          className="form-control"
          rows={3}
          value={createDescription}
          onChange={e => setCreateDescription(e.target.value)}
          placeholder="Enter event details"
        />
      </div>
      {createEventType == 5 && <div className="mb-1">
        <label className="form-label mb-1">Order Number (Optional)</label>
        <input
          type="number"
          className="form-control"
          value={createOrderNumber}
          onChange={e => setCreateOrderNumber(e.target.value)}
          min={1}
        />
      </div>}
      {createEventType == 12 && <div className="mb-1">
        <label className="form-label mb-1">Quote Number (Optional)</label>
        <input
          type="number"
          className="form-control"
          value={createQuoteNumber}
          onChange={e => setCreateQuoteNumber(e.target.value)}
          min={1}
        />
      </div>}
      <DialogActionsBar layout="end">
        <Button onClick={onClose} disabled={creatingEvent}>Cancel</Button>
        <Button
          themeColor="primary"
          onClick={handleCreateEvent}
          disabled={creatingEvent || !createDescription.trim()}
        >
          {creatingEvent ? "Creating..." : "Create"}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
